"use client";

/**
 * OperationSuccessDisplay - Display for CRUD operations
 *
 * Shows success/error state with a message and optional details.
 * Used for create, update, delete operations.
 */

import { useState } from "react";
import { Alert, Tag } from "@/ui";
import { colors, spacing, radius, textStyles, typography, primitives } from "@/ui/tokens";
import { CheckCircle } from "@mui/icons-material";
import {
  ToolCard,
  ToolHeader,
  ToolBody,
  ToolProgress,
  ToolError,
} from "../../Tool";
import { deriveToolState } from "@/lib/sola/types/tools";
import type { ToolDisplayProps } from "@/lib/sola/types/tools";

interface OperationResult {
  success?: boolean;
  status?: "success" | "error";
  message?: string;
  error?: string;
  data?: unknown;
  id?: string | number;
  [key: string]: unknown;
}

export function OperationSuccessDisplay({ toolCall, isStreaming }: ToolDisplayProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const toolState = deriveToolState(toolCall);

  const { status, isLoading, isError, error } = toolState;
  const { toolName, title } = toolCall;
  const result = toolCall.finalOutput as OperationResult | undefined;

  // Determine operation success from result
  const isSuccess =
    result?.success === true ||
    result?.status === "success" ||
    (status === "complete" && !isError && !result?.error);

  const operationMessage =
    result?.message ||
    result?.error ||
    (isSuccess ? "Operation completed successfully" : "Operation failed");

  return (
    <ToolCard status={status} noPadding>
      <ToolHeader
        title={title || ""}
        toolName={toolName}
        status={status}
        isExpanded={isExpanded}
        onToggleExpand={() => setIsExpanded((prev) => !prev)}
        icon={<CheckCircle style={{ fontSize: 18 }} />}
      />

      <ToolBody isOpen={isExpanded}>
        {/* Loading state */}
        {isLoading && (
          <div style={{ paddingTop: spacing[4], paddingBottom: spacing[4] }}>
            <ToolProgress variant="dots" label="Processing operation..." />
          </div>
        )}

        {/* Error state from execution */}
        {isError && error && <ToolError error={error} />}

        {/* Operation result */}
        {!isLoading && result && (
          <Alert
            variant={isSuccess ? "success" : "error"}
            title={isSuccess ? "Success" : "Error"}
          >
            <span style={{ ...textStyles.body2 }}>{operationMessage}</span>

            {/* Show ID if available */}
            {result?.id && (
              <div style={{ marginTop: spacing[2] }}>
                <Tag variant="default" size="sm" color="neutral">
                  ID: {result.id}
                </Tag>
              </div>
            )}
          </Alert>
        )}

        {/* Additional data (collapsed by default) */}
        {!isLoading && result?.data && (
          <div style={{ marginTop: result ? spacing[4] : 0 }}>
            <span
              style={{
                display: "block",
                marginBottom: spacing[2],
                ...textStyles.label,
                fontWeight: typography.fontWeight.medium,
                color: colors.text.muted,
              }}
            >
              Details
            </span>
            <pre
              style={{
                padding: spacing[3],
                borderRadius: radius.sm,
                backgroundColor: primitives.neutral[100],
                fontFamily: typography.fontFamily.mono,
                fontSize: `${typography.scale.xs[0]}px`,
                overflow: "auto",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                maxHeight: 200,
                margin: 0,
              }}
            >
              {JSON.stringify(result.data, null, 2)}
            </pre>
          </div>
        )}
      </ToolBody>
    </ToolCard>
  );
}

export default OperationSuccessDisplay;
